<template lang="pug">
v-card
  v-card-title
    span Punto de equilibrio
    ShowInfoBtn(@click="$refs.infoAlert.showAlert()")
  v-card-text
    InfoAlert(:message="alertMessage" ref="infoAlert")

    //- Costos fijos (5 categorías amplias)
    .section-label Costos fijos mensuales (indirectos)
    v-row(dense)
      v-col(cols="12" sm="6" md="4" v-for="(c, i) in categories" :key="i")
        v-text-field(
          v-model="c.category"
          dense outlined hide-details
          class="mb-2"
          :placeholder="'Categoría ' + (i+1)")
        v-text-field(
          v-model.number="c.monthly_amount"
          dense outlined hide-details
          type="number" min="0" prefix="$"
          class="mb-3")
    .d-flex.align-center.mb-4
      span.text--secondary.mr-4 Total fijo: <strong>{{ (fixedMonthly) | moneyMM }}/mes</strong> · {{ fixedAnnual | moneyMM }}/año
      v-spacer
      v-btn(small color="primary" outlined :loading="saving" @click="save") Guardar costos fijos

    v-divider.mb-4

    template(v-if="hasBase")
      //- Controles de escenario
      v-row.mb-2(align="center")
        v-col(cols="12" md="4")
          v-select(v-model="base" :items="baseOptions" label="Base de venta" dense outlined hide-details @change="fetchBase")
        v-col(cols="12" md="4")
          v-select(v-model="driver" :items="driverOptions" label="Prorrateo de fijos" dense outlined hide-details)
        v-col(cols="12" md="4")
          .prop-label ¿Qué % del plan esperas cumplir?  <strong>{{ proportion }}%</strong>
          v-slider(v-model="proportion" min="40" max="120" step="5" hide-details color="primary" track-color="grey lighten-2")

      //- Resumen global
      v-alert(dense text :color="globalColor" border="left" class="mb-4")
        span(v-html="globalMessage")

      v-row.mb-2
        v-col(cols="6" md="3")
          .metric
            .metric-label Punto de equilibrio
            .metric-value {{ breakEvenRevenue | moneyMM }}
            .metric-sub {{ breakEvenPctOfPlan.toFixed(0) }}% de tu plan
        v-col(cols="6" md="3")
          .metric
            .metric-label Utilidad neta
            .metric-value(:class="netProfit >= 0 ? 'pos' : 'neg'") {{ netProfit | moneyMM }}
            .metric-sub venta − costos − fijos
        v-col(cols="6" md="3")
          .metric
            .metric-label Margen de seguridad
            .metric-value {{ marginOfSafety.toFixed(0) }}%
            .metric-sub colchón sobre el equilibrio
        v-col(cols="6" md="3")
          .metric
            .metric-label Venta del plan
            .metric-value {{ planRevenue | moneyMM }}
            .metric-sub al {{ proportion }}% del óptimo

      //- Tabla por producto
      .section-label.mt-2 Equilibrio por producto ({{ driverLabel }})
      .tbl-wrap
        table
          thead
            tr
              th Producto
              th.num Equilibrio ($)
              th.num Equilibrio (unid.)
              th.num % de su plan
          tbody
            tr(v-for="p in perProduct" :key="p.name")
              td {{ p.name }}
              td.num {{ p.beRevenue | moneyMM }}
              td.num {{ formatUnits(p.beUnits) }}
              td.num
                span.pill(:class="p.cls") {{ p.pct.toFixed(0) }}%

    .empty-be(v-else)
      p.text--secondary.mb-0 Carga tus costos fijos arriba para calcular tu punto de equilibrio.
</template>

<script>
import { mapActions } from 'vuex'
import { fmtMoneyMM } from '@/utils/customVueFilters'
import InfoAlert from '../../common/InfoAlert.vue'
import ShowInfoBtn from '../../common/ShowInfoBtn.vue'

const DEFAULT_CATEGORIES = [
  'Arriendo e instalaciones',
  'Remuneraciones administrativas',
  'Servicios y suministros',
  'Administración y finanzas',
  'Otros',
]

export default {
  props: {
    filters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    projection: {
      type: Number,
      default: null,
    },
  },
  components: {
    InfoAlert,
    ShowInfoBtn,
  },
  data() {
    return {
      categories: DEFAULT_CATEGORIES.map(category => ({ category, monthly_amount: 0 })),
      products: [],
      totals: { revenue: 0, contribution: 0 },
      base: 'optimal',
      driver: 'revenue',
      proportion: 100,
      saving: false,
      baseOptions: [
        { text: 'Plan óptimo (Mix Óptimo)', value: 'optimal' },
        { text: 'Plan de ventas (seguimiento)', value: 'sale_plan' },
      ],
      driverOptions: [
        { text: 'Prorratear por venta', value: 'revenue' },
        { text: 'Prorratear por costo directo (producción)', value: 'direct_cost' },
      ],
      alertMessage: `
<p>
El punto de equilibrio es <b>cuánto tienes que vender para no perder</b>: el punto donde tu margen de contribución alcanza justo para pagar tus costos fijos. Los <b>costos directos (variables)</b> ya están en tu margen; aquí sumas los <b>costos fijos (indirectos)</b> y los reparte entre tus productos. Sobre el punto de equilibrio, cada peso es utilidad.
</p>`,
    }
  },
  computed: {
    fixedMonthly() {
      return this.categories.reduce((a, c) => a + (Number(c.monthly_amount) || 0), 0)
    },
    fixedAnnual() {
      return this.fixedMonthly * 12
    },
    hasBase() {
      return this.totals.revenue > 0 && this.fixedAnnual > 0
    },
    cmr() {
      return this.totals.revenue ? this.totals.contribution / this.totals.revenue : 0
    },
    totalDirectCost() {
      return this.totals.revenue - this.totals.contribution
    },
    p() {
      return this.proportion / 100
    },
    planRevenue() {
      return this.totals.revenue * this.p
    },
    planContribution() {
      return this.totals.contribution * this.p
    },
    breakEvenRevenue() {
      return this.cmr ? this.fixedAnnual / this.cmr : 0
    },
    breakEvenPctOfPlan() {
      return this.planRevenue ? (this.breakEvenRevenue / this.planRevenue) * 100 : 0
    },
    netProfit() {
      return this.planContribution - this.fixedAnnual
    },
    marginOfSafety() {
      if (!this.planRevenue) return 0
      const m = (1 - this.breakEvenRevenue / this.planRevenue) * 100
      return m
    },
    driverLabel() {
      return this.driver === 'revenue' ? 'prorrateo por venta' : 'prorrateo por costo directo'
    },
    globalColor() {
      if (this.netProfit < 0) return 'error'
      if (this.marginOfSafety < 20) return 'warning'
      return 'success'
    },
    globalMessage() {
      const be = Math.round(this.breakEvenPctOfPlan)
      if (this.netProfit < 0) {
        return `Al ${this.proportion}% de tu plan <b>no cubres tus costos fijos</b>: necesitas vender el <b>${be}%</b> ($${this.short(this.breakEvenRevenue)}) para no perder. Te faltan ${this.money(-this.netProfit)}.`
      }
      return `Necesitas vender el <b>${be}%</b> de tu plan ($${this.short(this.breakEvenRevenue)}) para no perder. A este ritmo tu utilidad neta es <b>${this.money(this.netProfit)}</b> con un colchón del <b>${Math.round(this.marginOfSafety)}%</b>.`
    },
    perProduct() {
      const totalWeight = this.driver === 'revenue' ? this.totals.revenue : this.totalDirectCost
      return this.products.map(pr => {
        const directCost = pr.revenue - pr.contribution
        const weight = totalWeight ? (this.driver === 'revenue' ? pr.revenue : directCost) / totalWeight : 0
        const fixedI = this.fixedAnnual * weight
        const cmrI = pr.revenue ? pr.contribution / pr.revenue : 0
        const beRevenue = cmrI ? fixedI / cmrI : 0
        const unitContribution = pr.quantity ? pr.contribution / pr.quantity : 0
        const beUnits = unitContribution ? fixedI / unitContribution : 0
        const planRev = pr.revenue * this.p
        const pct = planRev ? (beRevenue / planRev) * 100 : 0
        let cls = 'pill--ok'
        if (pct >= 85) cls = 'pill--risk'
        else if (pct >= 65) cls = 'pill--warn'
        return { name: pr.product_name, beRevenue, beUnits, pct, cls }
      })
    },
  },
  methods: {
    ...mapActions('commercial_projection', ['getBreakEvenBase', 'getFixedCosts', 'setFixedCosts']),
    short(v) {
      // Notación unificada MM, pero sin el símbolo (los callers anteponen "$").
      return fmtMoneyMM(v).replace('$', '')
    },
    money(v) {
      return fmtMoneyMM(v)
    },
    formatUnits(u) {
      return Math.round(u).toLocaleString('es-CL') + ' u'
    },
    async loadFixedCosts() {
      const { data, error } = await this.getFixedCosts()
      if (!error && data && data.length) {
        // completa hasta 5 filas para mantener el espacio de categorías
        const rows = data.map(d => ({ category: d.category, monthly_amount: d.monthly_amount }))
        while (rows.length < 5) rows.push({ category: DEFAULT_CATEGORIES[rows.length] || '', monthly_amount: 0 })
        this.categories = rows
      }
    },
    async fetchBase() {
      const params = {
        ...this.filters,
        projection: this.projection || this.$route.params.id,
        base: this.base,
      }
      const { data, error } = await this.getBreakEvenBase(params)
      if (!error && data) {
        this.products = data.products || []
        this.totals = data.totals || { revenue: 0, contribution: 0 }
      }
    },
    async save() {
      this.saving = true
      const items = this.categories
        .filter(c => c.category && (Number(c.monthly_amount) || 0) >= 0)
        .map((c, i) => ({ category: c.category, monthly_amount: Number(c.monthly_amount) || 0, order: i }))
      await this.setFixedCosts({ data: { items } })
      this.saving = false
      if (this.$toast) this.$toast.success('Costos fijos guardados')
    },
  },
  watch: {
    filters: {
      handler() { this.fetchBase() },
      deep: true,
    },
  },
  created() {
    this.loadFixedCosts()
    this.fetchBase()
  },
}
</script>

<style scoped>
.section-label { font-size: 12px; letter-spacing: .04em; text-transform: uppercase; font-weight: 700; color: #4A5A66; margin-bottom: 10px; }
.prop-label { font-size: 12.5px; color: #4A5A66; margin-bottom: 2px; }
.metric { border: 1px solid #ECEDF0; border-radius: 12px; padding: 12px 14px; height: 100%; }
.metric-label { font-size: 11.5px; color: #8A99A3; }
.metric-value { font-size: 19px; font-weight: 700; color: #1B2E38; font-variant-numeric: tabular-nums; margin-top: 2px; }
.metric-value.pos { color: #0FA890; } .metric-value.neg { color: #E0533C; }
.metric-sub { font-size: 11px; color: #8A99A3; margin-top: 2px; }
.tbl-wrap { overflow-x: auto; border: 1px solid #ECEDF0; border-radius: 12px; }
table { border-collapse: collapse; width: 100%; font-size: 13.5px; min-width: 460px; }
th, td { text-align: left; padding: 10px 14px; border-bottom: 1px solid #F0F2F7; }
th { font-size: 10.5px; letter-spacing: .05em; text-transform: uppercase; color: #8A99A3; font-weight: 600; background: #FAFAFB; }
th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; }
tr:last-child td { border-bottom: none; }
.pill { font-size: 12px; font-weight: 700; padding: 2px 9px; border-radius: 999px; }
.pill--ok { color: #0FA890; background: #E1F6F1; }
.pill--warn { color: #C9821B; background: #FBF0DC; }
.pill--risk { color: #E0533C; background: #FCE7E1; }
.empty-be { border: 1px dashed #D6DAE3; border-radius: 10px; padding: 22px; text-align: center; background: #FAFAFB; }
</style>
